"use client"

import { FormEvent, useCallback, useRef, useState } from "react"
import { subscribeNewsletter } from "@lib/data/newsletter"
import type { PublicStorefrontSettings } from "../../types"
import HCaptchaWidget, { type HCaptchaWidgetHandle } from "../hcaptcha-widget"
import styles from "../footer/footer.module.css"

type NewsletterSettings = PublicStorefrontSettings["footer"]["newsletter"]
type FormState = "idle" | "captcha-required" | "submitting" | "success" | "error"

export default function NewsletterForm({ settings }: { settings: NewsletterSettings }) {
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [token, setToken] = useState("")
  const [state, setState] = useState<FormState>("idle")
  const [message, setMessage] = useState("")
  const captchaRef = useRef<HCaptchaWidgetHandle>(null)

  const onCaptchaToken = useCallback((nextToken: string) => {
    setToken(nextToken)
    setState((current) => current === "captcha-required" ? "idle" : current)
    setMessage("")
  }, [])

  const onCaptchaExpired = useCallback(() => {
    setToken("")
  }, [])

  const onCaptchaError = useCallback(() => {
    setToken("")
    setState("error")
    setMessage("hCaptcha doğrulaması kullanılamıyor. Lütfen tekrar deneyin.")
  }, [])

  const resetCaptcha = () => {
    setToken("")
    captchaRef.current?.reset()
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (state === "submitting") return

    if (!token) {
      setState("captcha-required")
      setMessage("Lütfen hCaptcha doğrulamasını tamamlayın.")
      return
    }

    setState("submitting")
    setMessage("")

    try {
      const result = await subscribeNewsletter({
        email,
        hcaptchaToken: token,
        company,
      })
      setState("success")
      setMessage(result.message || "Aboneliğiniz alındı.")
      setEmail("")
      setCompany("")
    } catch (error) {
      setState("error")
      setMessage(error instanceof Error
        ? error.message
        : "Abonelik isteği gönderilemedi. Lütfen tekrar deneyin.")
    } finally {
      resetCaptcha()
    }
  }

  return (
    <form className={styles.newsletterForm} onSubmit={submit} noValidate={false}>
      <label className={styles.srOnly} htmlFor="newsletter-email">E-posta adresi</label>
      <input
        id="newsletter-email"
        className={styles.newsletterInput}
        type="email"
        required
        autoComplete="email"
        placeholder={settings.placeholder || "E-posta adresi"}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        disabled={state === "submitting"}
      />

      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor="newsletter-company">Şirket</label>
        <input
          id="newsletter-company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(event) => setCompany(event.target.value)}
        />
      </div>

      {settings.site_key && (
        <div className={styles.captchaWrap}>
          <HCaptchaWidget
            ref={captchaRef}
            siteKey={settings.site_key}
            onToken={onCaptchaToken}
            onExpired={onCaptchaExpired}
            onError={onCaptchaError}
          />
        </div>
      )}

      <button
        className={styles.newsletterButton}
        type="submit"
        disabled={state === "submitting"}
      >
        {state === "submitting" ? "Gönderiliyor…" : settings.button_label || "Abone ol"}
      </button>

      {settings.consent_text && (
        <p className={styles.consent}>{settings.consent_text}</p>
      )}

      {message && (
        <p
          className={state === "success" ? styles.success : styles.formError}
          role={state === "success" ? "status" : "alert"}
        >
          {message}
        </p>
      )}
    </form>
  )
}
